import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultAPIResult } from 'types/state.interface';
import type { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  type TAvailabilityRelationType,
  createEmptyAvailabilityRelationType,
} from '@timeedit/types';
import api from '../services/api.service';

// TODO: Figure out the proper type of Limitation
type Limitations = Record<string, unknown>;

type TAvailabilityRelationState = {
  loading: boolean;
  hasErrors: boolean;
  allAvailabilityRelations: TAvailabilityRelationType[];
  limitations: Limitations;
  availabilityRelationMap: Record<number, TAvailabilityRelationType>;
  firstAvailabilityRelation: TAvailabilityRelationType | null;
  secondAvailabilityRelation: TAvailabilityRelationType | null;
  selectedAvailabilityRelation?: TAvailabilityRelationType;
};

export const initialState: TAvailabilityRelationState = {
  loading: false,
  hasErrors: false,
  allAvailabilityRelations: [],
  limitations: {},
  availabilityRelationMap: {},
  firstAvailabilityRelation: null,
  secondAvailabilityRelation: null,
};

// Slice
const slice = createSlice({
  name: 'availabilityTypes',
  initialState,
  reducers: {
    fetchAvailabilityRelationsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAvailabilityRelationsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TAvailabilityRelationType>>
    ) => {
      state.allAvailabilityRelations = payload.results;
      state.availabilityRelationMap = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchAvailabilityRelationsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteAvailabilityRelationRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteAvailabilityRelationSuccess: (
      state,
      { payload: availabilityRelationId }: PayloadAction<number>
    ) => {
      state.allAvailabilityRelations = state.allAvailabilityRelations.filter(
        (availabilityType: TAvailabilityRelationType) =>
          availabilityType.id !== availabilityRelationId
      );
      state.availabilityRelationMap[availabilityRelationId] = undefined;
      finishedLoadingSuccess(state);
    },
    deleteAvailabilityRelationFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateAvailabilityRelationRequest: (state) => {
      isLoadingRequest(state);
    },
    updateAvailabilityRelationSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityRelationType>
    ) => {
      const index = state.allAvailabilityRelations.findIndex(
        (availabilityType: TAvailabilityRelationType) =>
          availabilityType.id === payload.id
      );
      state.allAvailabilityRelations[index] = payload;
      state.availabilityRelationMap[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    updateAvailabilityRelationFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstAvailabilityRelation: (
      state,
      { payload }: PayloadAction<TAvailabilityRelationType>
    ) => {
      const prevItem: TAvailabilityRelationType =
        state.firstAvailabilityRelation;
      state.firstAvailabilityRelation = payload;
      if (prevItem?.id === 0) {
        state.availabilityRelationMap[0] = undefined;
        state.allAvailabilityRelations = state.allAvailabilityRelations.filter(
          (ar: TAvailabilityRelationType) => ar.id !== 0
        );
      }
    },
    setSecondAvailabilityRelation: (
      state,
      { payload }: PayloadAction<TAvailabilityRelationType>
    ) => {
      state.secondAvailabilityRelation = payload;
    },
    createEmptyAvailabilityRelation: (state) => {
      if (state.firstAvailabilityRelation?.id === 0) return;
      const newItem = createEmptyAvailabilityRelationType();
      state.firstAvailabilityRelation = newItem;
      state.availabilityRelationMap[newItem.id] = newItem;
      state.allAvailabilityRelations.unshift(newItem);
    },
    duplicateAvailabilityRelation: (
      state,
      { payload }: PayloadAction<TAvailabilityRelationType>
    ) => {
      if (state.firstAvailabilityRelation?.id === 0) return;
      const newItem: TAvailabilityRelationType = {
        ...payload,
        id: 0,
        name: `${payload.name} (copy)`,
        extId: '',
      };
      state.firstAvailabilityRelation = newItem;
      state.availabilityRelationMap[newItem.id] = newItem;
      state.allAvailabilityRelations.unshift(newItem);
    },
    changeAvailabilityRelation: <
      Key extends keyof TAvailabilityRelationType =
        keyof TAvailabilityRelationType,
    >(
      state: TAvailabilityRelationState,
      { payload }: DrawerPayloadAction<TAvailabilityRelationType, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstAvailabilityRelation = {
          ...state.firstAvailabilityRelation,
          [key]: value,
        };
      } else {
        state.secondAvailabilityRelation = {
          ...state.secondAvailabilityRelation,
          [key]: value,
        };
      }
    },
    discardChanges: (
      state,
      { payload }: PayloadAction<{ id: number; isFirstDrawer: boolean }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstAvailabilityRelation = state.availabilityRelationMap[id];
      } else {
        state.secondAvailabilityRelation = state.availabilityRelationMap[id];
      }
    },
    closeDrawer: (state, { payload: id }: PayloadAction<number>) => {
      if (state.firstAvailabilityRelation?.id === id) {
        state.firstAvailabilityRelation = null;
      }
      if (state.secondAvailabilityRelation?.id === id) {
        state.secondAvailabilityRelation = null;
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const availabilityRelationsSelector = (state: TRootState) =>
  state.availabilityTypes.allAvailabilityRelations;
export const firstAvailabilityRelationSelector = (state: TRootState) =>
  state.availabilityTypes.firstAvailabilityRelation;
export const secondAvailabilityRelationSelector = (state: TRootState) =>
  state.availabilityTypes.secondAvailabilityRelation;

export const selectedAvailabilityRelationIdsSelector = createSelector(
  [
    (state: TRootState) => state.availabilityTypes.firstAvailabilityRelation,
    (state: TRootState) => state.availabilityTypes.secondAvailabilityRelation,
  ],
  (first, second) => _.compact([first, second]).map((ar) => ar.id)
);

export const availabilityRelationMapSelector = (state: TRootState) =>
  state.availabilityTypes.availabilityRelationMap;
export const availabilityRelationLoadingSelector = (state: TRootState) =>
  state.availabilityTypes.loading;

// Actions
export const {
  fetchAvailabilityRelationsRequest,
  fetchAvailabilityRelationsSuccess,
  fetchAvailabilityRelationsFailure,
  deleteAvailabilityRelationRequest,
  deleteAvailabilityRelationSuccess,
  deleteAvailabilityRelationFailure,
  updateAvailabilityRelationRequest,
  updateAvailabilityRelationSuccess,
  updateAvailabilityRelationFailure,
  setFirstAvailabilityRelation,
  setSecondAvailabilityRelation,
  createEmptyAvailabilityRelation,
  duplicateAvailabilityRelation,
  changeAvailabilityRelation,
  discardChanges,
  closeDrawer,
} = slice.actions;

export const fetchAvailabilityRelations = createAppAsyncThunk(
  'availabilityTypes/fetchAvailabilityRelations',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAvailabilityRelationsRequest());
      const availabilityTypes = await api.get({
        endpoint: `/availability-types`,
      });
      dispatch(fetchAvailabilityRelationsSuccess(availabilityTypes));
    } catch (e) {
      dispatch(fetchAvailabilityRelationsFailure());
      return console.error(e);
    }
  }
);

export const deleteAvailabilityRelation = createAppAsyncThunk<void, number>(
  'availabilityTypes/fetchAvailabilityRelations',
  async (availabilityTypeId, { dispatch }) => {
    try {
      dispatch(deleteAvailabilityRelationRequest());
      await api.delete({
        endpoint: `/availability-types/${availabilityTypeId}`,
      });
      dispatch(deleteAvailabilityRelationSuccess(availabilityTypeId as any));
      dispatch(closeDrawer(availabilityTypeId));
    } catch (e) {
      dispatch(deleteAvailabilityRelationFailure());
      return console.error(e);
    }
  }
);

export const saveAvailabilityRelation = createAppAsyncThunk<
  void,
  TAvailabilityRelationType
>(
  'availabilityTypes/fetchAvailabilityRelations',
  async (availabilityType, { dispatch }) => {
    try {
      dispatch(updateAvailabilityRelationRequest());
      const { id, ...availabilityRelationBody } = availabilityType;
      if (id !== 0) {
        const response = await api.patch({
          endpoint: `/availability-types/${id}`,
          data: availabilityRelationBody,
        });
        dispatch(updateAvailabilityRelationSuccess(response));
      } else {
        await api.post({
          endpoint: `/availability-types`,
          data: availabilityRelationBody,
        });
        dispatch(fetchAvailabilityRelations());
      }
      dispatch(closeDrawer(id));
    } catch (e: any) {
      dispatch(updateAvailabilityRelationFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkFirstAvailabilityRelationExists =
  (): TAppThunk<boolean> => (dispatch, getState) =>
    !!getState().availabilityTypes.firstAvailabilityRelation;

export const checkAvailabilityRelationSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (dispatch, getState) => {
    const { availabilityTypes } = getState();
    const {
      firstAvailabilityRelation,
      secondAvailabilityRelation,
      availabilityRelationMap,
    } = availabilityTypes;
    if (
      isFirstDrawer &&
      firstAvailabilityRelation &&
      availabilityRelationMap[firstAvailabilityRelation.id]
    ) {
      return checkSavedDisabled(
        firstAvailabilityRelation,
        availabilityRelationMap[firstAvailabilityRelation.id]
      );
    }
    if (
      !isFirstDrawer &&
      secondAvailabilityRelation &&
      availabilityRelationMap[secondAvailabilityRelation.id]
    ) {
      return checkSavedDisabled(
        secondAvailabilityRelation,
        availabilityRelationMap[secondAvailabilityRelation.id]
      );
    }

    return true;
  };
